/* eslint-disable no-underscore-dangle */
import React, { useState, useContext, useEffect, useCallback } from "react";
import { format, isValid } from "date-fns";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { createPayment } from "api/Stripe";
import { UserContext } from "context/user";
import Button from "components/Button";
import "./index.scss";
import InputField from "components/Input";
import Loading from "components/Loading";
import stripeLogo from "assets/logo/stripe.svg";
import AddressFields from "components/AddressFields";
import { getPricing } from "utils";
import { StripeContext } from "context/stripe";
import ContentLoader from "components/ContentLoader";
import {
  createContactDetails,
  getContactDetails,
  updateContactDetails,
} from "api/User";
import { SUCCESS_STATUS } from "constants/success-status";

const PRODUCT_ID = "price_1L8f5vKz9OIDP8dg6xf5T0L8";
const SplitForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const history = useHistory();
  const { token, user } = useContext(UserContext);
  const { state } = useContext(StripeContext);
  const { pricingDetails, isFetchingPricingDetails } = state;
  const [userDetails, setUserDetails] = useState(null);
  const [formType, setFormType] = useState("");

  const [billingDetails, setBillingDetails] = useState({
    name: "",
    companyName: "",
    address1: "",
    address2: "",
    city: "",
    zipCode: "",
    country: "",
    state: "",
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [cardErrorMessage, setCardErrorMessage] = useState({
    name: "",
    cardNumber: "",
    cardExpiry: "",
    cardCvc: "",
  });

  const [billingDetailsError, setBillingDetailsError] = useState({
    companyName: "",
    address1: "",
    country: "",
    state: "",
    city: "",
    zipCode: "",
  });

  const [hideCvc, setHideCvc] = useState(false);

  const isErrorListHasValue = (errorList) => {
    const isListHasError = Object.values(errorList).some(
      (error) => error !== null && error !== "" && typeof error !== "undefined"
    );
    return isListHasError;
  };

  const fetchData = useCallback(async () => {
    if (!token) return;

    const userDetailsData = await getContactDetails(token);
    setUserDetails(userDetailsData.data);
  }, [token]);

  useEffect(() => {
    if (userDetails) {
      setBillingDetails({
        name: userDetails?.first_name || "",
        companyName: "",
        address1: userDetails?.address1 || "",
        address2: userDetails?.address2 || "",
        city: userDetails?.city || "",
        zipCode: userDetails?.post_code || "",
        country: userDetails?.country || "",
        state: userDetails?.state || "",
        first_name: userDetails?.first_name || "",
        last_name: userDetails?.last_name || "",
      });
      setFormType("update");
    } else {
      setFormType("add");
    }
  }, [userDetails]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const validateField = (fieldName, value) => {
    let error;
    if (fieldName === "name") {
      error = value ? "" : "Enter cardholder name";
    } else if (fieldName === "address1") {
      error = value ? "" : "Enter address";
    } else if (fieldName === "city") {
      error = value ? "" : "Enter city";
    } else if (fieldName === "country") {
      error = value ? "" : "Please select country";
    } else if (fieldName === "state") {
      error = value ? "" : "Please select state/province.";
    } else if (fieldName === "zipCode") {
      error = value ? "" : "Please enter zip or postal code.";
    }
    return error;
  };

  const handleSelectCountry = (val) => {
    const newField = { ...billingDetails, country: val };
    setBillingDetails(newField);
    const fieldError = validateField("country", val);
    const newError = {
      ...billingDetailsError,
      country: fieldError || "",
    };
    setBillingDetailsError(newError);
  };

  // const handleSelectCity = (val) => {
  //   const newField = { ...billingDetails, state: val };
  //   setBillingDetails(newField);
  //   const fieldError = validateField("state", val);
  //   const newError = {
  //     ...billingDetailsError,
  //     state: fieldError || "",
  //   };
  //   setBillingDetailsError(newError);
  // };

  const validateOnSubmit = (objectList) => {
    const errorList = {};
    Object.keys(objectList).forEach((key) => {
      if (key !== "name") {
        errorList[key] = validateField(key, objectList[key]);
      }
    });
    setBillingDetailsError(errorList);
    return errorList;
  };

  const validateStripeElement = () => {
    // get reference to the card object
    const cardNumberElement = elements?.getElement(CardNumberElement);
    const cardExpiryElement = elements?.getElement(CardExpiryElement);
    const cardCvcElement = elements?.getElement(CardCvcElement);

    const newCardError = {
      ...cardErrorMessage,
    };

    // validate card holder name
    if (billingDetails?.name === "") {
      newCardError.name = "Please input card holder name.";
    }
    // validate card number
    if (cardNumberElement?._empty) {
      newCardError.cardNumber = "Please input your card details.";
    } else if (
      cardNumberElement?._complete === false ||
      cardNumberElement?._invalid
    ) {
      newCardError.cardNumber = "Please enter a valid card number";
    }
    // validate card expiry
    if (cardExpiryElement?._empty) {
      newCardError.cardExpiry = "Please input your card expiry details.";
    } else if (
      cardExpiryElement?._complete === false ||
      cardExpiryElement?._invalid
    ) {
      newCardError.cardExpiry = "Card expiry is invalid.";
    }

    // validate card cvc
    if (cardCvcElement?._empty) {
      newCardError.cardCvc = "Please input your card cvc.";
    } else if (
      cardCvcElement?._complete === false ||
      cardCvcElement?._invalid
    ) {
      newCardError.cardCvc =
        "Please copy your CVV/CVC code from the back of your card and continue with your payment.";
    }
    setCardErrorMessage(newCardError);
    return newCardError;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    const cardNumberElement = elements?.getElement(CardNumberElement);
    const billingDetailsErrorList = validateOnSubmit(billingDetails);
    const cardDetailsErrorList = validateStripeElement();

    if (
      isErrorListHasValue(billingDetailsErrorList) ||
      isErrorListHasValue(cardDetailsErrorList)
    ) {
      toast.error("There's some validation error");
      return;
    }
    const data = {
      stripe_product_id: PRODUCT_ID,
    };

    setIsProcessing(true);

    const params = {
      data: {
        first_name: billingDetails?.first_name || "",
        last_name: billingDetails?.last_name || "",
        email: userDetails?.email || user?.email,
        address1: billingDetails?.address1,
        address2: billingDetails?.address2,
        city: billingDetails?.city,
        state: billingDetails?.state,
        country: billingDetails?.country,
        post_code: billingDetails?.zipCode,
        phone_number: userDetails?.phone_number || "",
        user_type: userDetails?.user_type || "other",
      },
    };

    let response = {};
    if (formType === "add") {
      response = await createContactDetails(params, token);
    } else {
      response = await updateContactDetails(params, token);
    }
    if (SUCCESS_STATUS.includes(response.status)) {
      // get stripe client_secret
      const { stripe_payment_intent_client_secret: clientSecret } =
        await createPayment(data, token);
      // proceed to payment
      const paymentMethodRequest = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberElement,
        billing_details: {
          address: {
            city: billingDetails?.state,
            country: billingDetails?.country,
            line1: billingDetails?.streetAddress,
            line2: billingDetails?.suburb,
            postal_code: billingDetails?.zipCode,
          },
          name: billingDetails?.companyName,
        },
      });
      // check for payment status
      const confirmedCardPayment = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: paymentMethodRequest?.paymentMethod?.id,
        }
      );
      if (confirmedCardPayment?.paymentIntent?.status === "succeeded") {
        history.replace("/advanced/success");
        window.location.reload();
      } else if (confirmedCardPayment?.error) {
        toast.error(confirmedCardPayment?.error?.message);
        setIsProcessing(false);
      }
    }
  };

  const validateCardOnchange = (e) => {
    const newError = {
      ...cardErrorMessage,
      [e?.elementType]: e?.error?.message || "",
    };
    setCardErrorMessage(newError);
  };

  const cardNumberElementOptions = {
    showIcon: true,
    style: {
      base: {
        fontSize: "16px",
        backgroundColor: "#fff",
        color: "#292b33",
        "::placeholder": {
          color: "#8a8e99",
          fontWeight: "300",
        },
      },
    },
    placeholder: "---- ---- ---- ----",
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: "16px",
        backgroundColor: "#fff",
        color: "#292b33",
        "::placeholder": {
          color: "#8a8e99",
          fontWeight: "300",
        },
      },
    },
  };

  const validateInput = (e) => {
    e.preventDefault();
    const { value, name } = e.target;
    const fieldError = validateField(name, value);

    if (name === "name") {
      const newError = {
        ...cardErrorMessage,
        [name]: fieldError || "",
      };
      setCardErrorMessage(newError);
      return;
    }
    const newError = {
      ...billingDetailsError,
      [name]: fieldError || "",
    };
    setBillingDetailsError(newError);
  };

  const handleInputChange = (event) => {
    event.preventDefault();
    validateInput(event);
    const { value, name } = event.target;
    const newField = { ...billingDetails, [name]: value };
    setBillingDetails(newField);
  };

  const {
    actualPrice,
    basePrice,
    discountValue,
    discountValidity,
    taxRate,
    isValidDiscount,
  } = getPricing(pricingDetails);

  const getOfferValidity = () => {
    let formattedDate = "";
    const tax = taxRate;
    if (isValid(discountValidity)) {
      formattedDate = format(new Date(discountValidity), "dd MMMM yyyy");
    } else {
      formattedDate = "";
    }
    return (
      <>
        <div className={`discount-wrapper ${tax ? "with-tax" : ""}`}>
          <span className="notes">Note</span>
          <div className="notes-wrapper">
            {formattedDate && (
              <p className="text">Offer runs until {formattedDate}</p>
            )}
            {tax && <p className="text">Excludes {`${tax}% tax`}</p>}
          </div>
        </div>
      </>
    );
  };

  const renderPrice = () => {
    return <h3 className="price">${basePrice}</h3>;
  };

  const renderDiscountedPrice = () => {
    return (
      <div className="description">
        ${actualPrice || 0} per year
        {basePrice && <span className="old"> from ${basePrice}</span>}
        {discountValue && (
          <p className="discount">{`${discountValue}`} Discount offer</p>
        )}
      </div>
    );
  };

  return (
    <>
      <form className="subscription-form" onSubmit={handleSubmit}>
        <h1 className="title">Subscribe to Advanced</h1>
        {isProcessing && (
          <div className="processing-loader">
            <p className="title">Processing Payment</p>
            <p className="description">
              It may take a few seconds to process your payment, Please wait.
            </p>
            <Loading enabled />
          </div>
        )}
        <section
          className={`billed-section ${isProcessing ? "-processing" : ""}`}
        >
          <div className="text-wrapper">
            <p className="text">Billed Yearly</p>
            {isFetchingPricingDetails && <ContentLoader />}
            {!isFetchingPricingDetails && (
              <>
                {isValidDiscount && discountValue
                  ? renderDiscountedPrice()
                  : renderPrice()}
              </>
            )}
          </div>
          <div className="circle" />
          <div className="billed-divider" />
          {isValidDiscount && getOfferValidity()}
        </section>

        <section
          className={`billing-details ${isProcessing ? "-processing" : ""}`}
        >
          <div className="text-wrapper">
            <p className="text">Billing Details</p>
          </div>
          <div className="form-section">
            <InputField
              id="companyName"
              placeholder="Ex. Acme"
              label="Company Name"
              value={billingDetails?.companyName}
              onChange={(e) => handleInputChange(e)}
              onBlur={(e) => validateInput(e)}
              error={billingDetailsError?.companyName}
            />
            <AddressFields
              user={billingDetails}
              country={billingDetails?.country}
              states={billingDetails?.state}
              handleSelectCountry={handleSelectCountry}
              isDisabled={false}
              handleInputChange={handleInputChange}
              errors={billingDetailsError}
            />
          </div>
        </section>

        <section className="stripe-wrapper">
          <div className="text-wrapper">
            <p className="text">Checkout using a debit or credit card</p>
          </div>
          <InputField
            id="name"
            placeholder="Cardholder Name"
            label="Cardholder Name"
            value={billingDetails?.name}
            onChange={(e) => handleInputChange(e)}
            onBlur={(e) => validateInput(e)}
            error={cardErrorMessage?.name}
          />

          <div className="input-field">
            {cardErrorMessage?.cardNumber && (
              <p className="custom-error">{cardErrorMessage?.cardNumber}</p>
            )}
            <div
              className={`custom-field ${
                cardErrorMessage?.cardNumber ? "-error" : ""
              }`}
            >
              <CardNumberElement
                id="cardNumber"
                options={cardNumberElementOptions}
                onChange={validateCardOnchange}
              />
            </div>
            <label htmlFor="cardNumber" className="label">
              Card Number
            </label>
          </div>

          <div className="field-group">
            <div className="input-field">
              {cardErrorMessage?.cardExpiry && (
                <p className="custom-error">{cardErrorMessage?.cardExpiry}</p>
              )}
              <div
                className={`custom-field ${
                  cardErrorMessage?.cardExpiry ? "-error" : ""
                }`}
              >
                <CardExpiryElement
                  id="cardExpiry"
                  options={cardElementOptions}
                  onChange={validateCardOnchange}
                />
              </div>
              <label htmlFor="cardExpiry" className="label">
                Card Expiry
              </label>
            </div>
            <div className="input-field">
              {cardErrorMessage?.cardCvc && (
                <p className="custom-error">{cardErrorMessage?.cardCvc}</p>
              )}
              <div
                className={`custom-field ${
                  cardErrorMessage?.cardCvc ? "-error" : ""
                } ${hideCvc ? "no-placeholder" : ""}`}
                id="cvc"
              >
                {hideCvc && <p>***</p>}
                <CardCvcElement
                  id="cardCvc"
                  options={cardElementOptions}
                  onChange={validateCardOnchange}
                  placeholder=""
                  maxLength={5}
                />
                <span
                  className={hideCvc ? "icon-eye-close" : "icon-eye"}
                  onClick={() => setHideCvc(!hideCvc)}
                  aria-hidden
                />
              </div>
              <label htmlFor="cardCvc" className="label">
                CVC/CVV
              </label>
            </div>
          </div>
        </section>
        <section className="pricing-section">
          <p className="text">
            Billed Now{" "}
            <img className="stripe-logo" src={stripeLogo} alt="alt" />
          </p>
          <div className="price-wrapper">
            <span className="currency">AUD</span>
            <p className="price">{`${actualPrice}`}</p>
          </div>
        </section>
        <p className="hint">
          All sales are charged in AUD and all sales are final. You will be
          charged {`$${actualPrice} AUD`} immediately. You will be charged
          {` $${basePrice} AUD `}
          every year thereafter while the subscription is active. Cancel any
          time.
        </p>

        <div className="divider" />
        <Button
          submit
          disabled={!stripe || isProcessing}
          color="green"
          type="solid"
          label={isProcessing ? "Processing" : "Subscribe & Checkout"}
        />
      </form>
    </>
  );
};

export default SplitForm;
